import { useParams, Link } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { Card } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"
import { useState } from "react"
import { CatalogEntry } from "@/types/database/opportunity"
import { OpportunityBookingPanel } from "@/components/opportunities/details/OpportunityBookingPanel"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { getOpportunityByCatalogSlug, getRelatedNPOOpportunities, getSimilarOpportunities } from "@/lib/queries/opportunities";
import SEO from '@/components/SEO';

function OpportunityCard({ 
  opportunity, 
  npoSlug, 
  showNpo = false,
  session,
  profile
}: { 
  opportunity: CatalogEntry; 
  npoSlug: string;
  showNpo?: boolean;
  session: any | null;
  profile: any | null;
}) {
  return (
    <Link
      to={`/opportunities/${npoSlug}/${opportunity.slug}`}
      className="block group"
    >
      <Card className="overflow-hidden h-full hover:shadow-lg transition-shadow">
        <div className="aspect-[4/3] relative overflow-hidden">
          {opportunity.images?.[0]?.image_url ? (
            <img
              src={opportunity.images[0].image_url}
              alt={opportunity.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div 
              className={cn(
                "w-full h-full flex items-center justify-center group-hover:scale-105 transition-transform duration-300",
                "bg-gradient-to-br from-blue-500 to-indigo-600"
              )}
            >
              <span className="text-white text-xl font-medium">
                {opportunity.title.charAt(0)}
              </span>
            </div>
          )}
        </div>
        <div className="p-4">
          <h3 className="font-semibold text-lg mb-2 line-clamp-2">
            {opportunity.title}
          </h3>
          {showNpo && opportunity.npo && (
            <div className="text-sm text-gray-600 mb-2">
              by {opportunity.npo.org_name}
            </div>
          )}
          <p className="text-gray-600 text-sm line-clamp-2">
            {opportunity.description}
          </p>
        </div>
      </Card>
    </Link>
  );
}

export default function OpportunityDetailsPage({profile, session}: {profile: any | null; session: any | null}) {
  const { npoSlug, catalogSlug } = useParams()
  const [currentImageIndex, setCurrentImageIndex] = useState(0)

  const { data: opportunity, isLoading } = useQuery({
    queryKey: ["opportunity", catalogSlug, npoSlug],
    queryFn: () => getOpportunityByCatalogSlug(catalogSlug!, npoSlug!),
    enabled: !!catalogSlug && !!npoSlug,
  })

  const { data: npoOpportunities } = useQuery({
    queryKey: ["npo-opportunities", opportunity?.npo_id],
    queryFn: () => getRelatedNPOOpportunities(opportunity.npo_id!, opportunity.id),
    enabled: !!opportunity?.npo_id,
  });

  const { data: similarOpportunities } = useQuery({
    queryKey: ["similar-opportunities", opportunity?.npo?.focus_areas],
    queryFn: () => getSimilarOpportunities(opportunity.npo_id),
    enabled: !!opportunity?.npo?.focus_areas,
  });

  if (isLoading) {
    return (
      <div className="container mx-auto py-8">
        <Skeleton className="h-[400px] w-full" />
      </div>
    )
  }

  if (!opportunity) {
    return (
      <div className="container mx-auto py-8">
        <Card className="p-6">
          <h1 className="text-2xl font-bold">Opportunity not found</h1>
          <p className="text-gray-600 mt-2">
            The opportunity you're looking for doesn't exist or has been removed.
          </p>
        </Card>
      </div>
    )
  }

  const nextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === (opportunity.opportunity_catalog_images?.length || 1) - 1 ? 0 : prev + 1
    )
  }

  const prevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? (opportunity.opportunity_catalog_images?.length || 1) - 1 : prev - 1
    )
  }

  return (
    <>
      <SEO 
        title={opportunity?.title || "Volunteer Opportunity"}
        description={opportunity?.description || "Learn more about this volunteer opportunity and how you can make a difference."}
      />
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-4">
          <div className="text-sm text-gray-600">
            <Link to="/opportunities" className="hover:text-blue-600">Opportunities</Link>
            <span className="mx-2">/</span>
            <Link to={`/opportunities/${npoSlug}`} className="hover:text-blue-600">
              {opportunity.npo?.org_name}
            </Link>
            <span className="mx-2">/</span>
            <span>{opportunity.title}</span>
          </div>
        </div>

        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-1">
              <div className="prose max-w-none mb-8">
                <h1 className="text-4xl font-bold mb-4">{opportunity.title}</h1>
                <p className="text-xl text-gray-600 mb-8">{opportunity.description}</p>
              </div>

              {opportunity.images && opportunity.images.length > 0 && (
                <div className="relative -mx-4 sm:-mx-6 lg:-mx-8 mb-12">
                  <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    {opportunity.images.length === 1 ? (
                      // Single image layout - full width
                      <div className="aspect-[16/9] relative rounded-lg overflow-hidden shadow-lg">
                        <img
                          src={opportunity.images[0].image_url}
                          alt={`${opportunity.title} - Main Image`}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                      </div>
                    ) : opportunity.images.length === 2 ? (
                      // Two images layout - split 60/40
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 aspect-[16/6]">
                        <div className="md:col-span-3 relative rounded-lg overflow-hidden shadow-lg">
                          <img
                            src={opportunity.images[0].image_url}
                            alt={`${opportunity.title} - Main Image`}
                            className="absolute inset-0 w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                        </div>
                        <div className="md:col-span-2 relative rounded-lg overflow-hidden shadow-lg">
                          <img
                            src={opportunity.images[1].image_url}
                            alt={`${opportunity.title} - Second Image`}
                            className="absolute inset-0 w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                        </div>
                      </div>
                    ) : (
                      // Three or more images layout - main image with two smaller ones
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 aspect-[16/6]">
                        <div className="md:col-span-2 relative rounded-lg overflow-hidden shadow-lg">
                          <img
                            src={opportunity.images[0].image_url}
                            alt={`${opportunity.title} - Main Image`}
                            className="absolute inset-0 w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                        </div>
                        <div className="hidden md:grid grid-rows-2 gap-4">
                          {opportunity.images.slice(1, 3).map((image, index) => (
                            <div key={index} className="relative rounded-lg overflow-hidden shadow-lg">
                              <img
                                src={image.image_url}
                                alt={`${opportunity.title} - Image ${index + 2}`}
                                className="absolute inset-0 w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                            </div>
                          ))}
                        </div>
                        {opportunity.images.length > 3 && (
                          <button
                            className="absolute bottom-4 right-4 bg-white/90 hover:bg-white px-4 py-2 rounded-full text-sm font-medium shadow-lg transition-colors"
                            onClick={() => {/* TODO: Implement gallery view */}}
                          >
                            +{opportunity.images.length - 3} more photos
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <Card className="p-6 mb-8">
                <Collapsible>
                  <CollapsibleTrigger className="flex items-center justify-between w-full">
                    <h2 className="text-xl font-semibold">Prerequisites</h2>
                    <ChevronDown className="h-5 w-5 text-gray-500 transition-transform duration-200" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-4">
                    <ul className="list-disc pl-5 space-y-2">
                      {opportunity.prerequisites.map((prerequisite, index) => (
                        <li key={index} className="text-gray-600">{prerequisite}</li>
                      ))}
                    </ul>
                  </CollapsibleContent>
                </Collapsible>
              </Card>

              <Card className="p-6 mb-8">
                <Collapsible>
                  <CollapsibleTrigger className="flex items-center justify-between w-full">
                    <h2 className="text-xl font-semibold">Timeline</h2>
                    <ChevronDown className="h-5 w-5 text-gray-500 transition-transform duration-200" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-4">
                    <ul className="list-disc pl-5 space-y-2">
                      {opportunity.timeline.map((item, index) => (
                        <li key={index} className="text-gray-600">{item}</li>
                      ))}
                    </ul>
                  </CollapsibleContent>
                </Collapsible>
              </Card>

              <Card className="p-6 mb-8">
                <Collapsible>
                  <CollapsibleTrigger className="flex items-center justify-between w-full">
                    <h2 className="text-xl font-semibold">What's Included</h2>
                    <ChevronDown className="h-5 w-5 text-gray-500 transition-transform duration-200" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-4">
                    <div className="prose max-w-none">
                      <p className="text-gray-600 whitespace-pre-wrap">{opportunity.whats_included}</p>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </Card>

              <Card className="p-6 mb-8">
                <Collapsible>
                  <CollapsibleTrigger className="flex items-center justify-between w-full">
                    <h2 className="text-xl font-semibold">Volunteer Support & Output</h2>
                    <ChevronDown className="h-5 w-5 text-gray-500 transition-transform duration-200" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-4">
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-medium mb-2">Support Provided</h3>
                        <p className="text-gray-600 whitespace-pre-wrap">
                          {opportunity.opportunity.volunteer_support}
                        </p>
                      </div>
                      <div>
                        <h3 className="font-medium mb-2">Expected Output</h3>
                        <p className="text-gray-600">
                          {opportunity.volunteer_output}
                        </p>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </Card>

              <Card className="p-6 mb-8">
                <Collapsible>
                  <CollapsibleTrigger className="flex items-center justify-between w-full">
                    <h2 className="text-xl font-semibold">Frequently Asked Questions</h2>
                    <ChevronDown className="h-5 w-5 text-gray-500 transition-transform duration-200" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-4">
                    <div className="prose max-w-none">
                      <p className="text-gray-600 whitespace-pre-wrap">{opportunity.faqs}</p>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </Card>

              <Card className="p-6 mb-8">
                <Collapsible>
                  <CollapsibleTrigger className="flex items-center justify-between w-full">
                    <h2 className="text-xl font-semibold">Terms & Conditions</h2>
                    <ChevronDown className="h-5 w-5 text-gray-500 transition-transform duration-200" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-4">
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-medium mb-2">Waiver Forms</h3>
                        <p className="text-gray-600 whitespace-pre-wrap">{opportunity.waiver_forms}</p>
                      </div>
                      <div>
                        <h3 className="font-medium mb-2">Terms & Conditions</h3>
                        <p className="text-gray-600 whitespace-pre-wrap">{opportunity.terms_and_conditions}</p>
                      </div>
                      <div>
                        <h3 className="font-medium mb-2">Cancellation Policy</h3>
                        <p className="text-gray-600 capitalize">{opportunity.cancellation_policy}</p>
                        <p className="text-gray-600 mt-2">{opportunity.cancellation_terms}</p>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </Card>

              {opportunity.npo && (
                <Card className="p-6 mb-8">
                  <Collapsible>
                    <CollapsibleTrigger className="flex items-center justify-between w-full">
                      <h2 className="text-xl font-semibold">About {opportunity.npo.org_name}</h2>
                      <ChevronDown className="h-5 w-5 text-gray-500 transition-transform duration-200" />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pt-4">
                      <div className="flex flex-col gap-4">
                        <div className="flex items-start gap-4">
                          {opportunity.npo.logo_url && (
                            <img
                              src={opportunity.npo.logo_url}
                              alt={opportunity.npo.org_name}
                              className="h-16 w-16 rounded-full"
                            />
                          )}
                          <div className="flex-1">
                            <p className="text-gray-600 mb-4">{opportunity.npo.description}</p>
                            
                            <div className="space-y-4">
                              <div>
                                <h3 className="font-medium mb-2">Focus Areas</h3>
                                <div className="flex flex-wrap gap-2">
                                  {opportunity.npo.meta?.focus_areas?.map((area) => (
                                    <span
                                      key={area}
                                      className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-sm"
                                    >
                                      {area}
                                    </span>
                                  ))}
                                </div>
                              </div>

                              <div>
                                <h3 className="font-medium mb-2">Beneficiary Types</h3>
                                <div className="flex flex-wrap gap-2">
                                  {opportunity.opportunity.beneficiary_types.map((type) => (
                                    <span
                                      key={type}
                                      className="px-2 py-1 bg-purple-50 text-purple-700 rounded-full text-sm"
                                    >
                                      {type}
                                    </span>
                                  ))}
                                </div>
                              </div>

                              {opportunity.npo.website && (
                                <div>
                                  <h3 className="font-medium mb-2">Website</h3>
                                  <a
                                    href={opportunity.npo.website}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline"
                                  >
                                    {opportunity.npo.website}
                                  </a>
                                </div>
                              )}

                              <div>
                                <h3 className="font-medium mb-2">Location</h3>
                                <p className="text-gray-600">
                                  {opportunity.npo.city}, {opportunity.npo.country}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </Card>
              )}
            </div>

            <div className="lg:w-80">
              <OpportunityBookingPanel profile={profile} session={session} opportunity={opportunity} npoSlug={npoSlug} catalogSlug={catalogSlug} />
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          {npoOpportunities && npoOpportunities.length > 0 && (
            <div className="mb-12">
              <h2 className="text-2xl font-bold mb-6">
                More opportunities from {opportunity?.npo?.org_name}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {npoOpportunities.map((opp) => (
                  <OpportunityCard 
                    key={opp.id} 
                    opportunity={opp} 
                    npoSlug={opp.npo?.slug || ''} 
                    session={session}
                    profile={profile}
                  />
                ))}
              </div>
            </div>
          )}

          {similarOpportunities && similarOpportunities.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold mb-6">Similar opportunities</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {similarOpportunities.map((opp) => (
                  <OpportunityCard 
                    key={opp.id} 
                    opportunity={opp} 
                    npoSlug={opp.npo?.slug || ''} 
                    showNpo 
                    session={session}
                    profile={profile}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
